import React, { useMemo } from 'react';
import { FilterItem, SelectOption } from '../../../utils/models';
import { matchPhrases } from '../../../utils/utilities';
import { MATCH_TYPES } from '../../../utils/constants';

import Button from '../../themed/button';
import Conditional from '../../common/conditional';
import Input from '../../themed/input/input';
import Select from '../../themed/select/select';

import classNames from './style.module.scss';
import toastService from '../../../services/toastService';

interface Props {
    filters: FilterItem[];
    onFilterChange: (newFilters: FilterItem[]) => void;
    filterOptions: SelectOption[];
}

const Filters: React.FC<Props> = ({ filters, filterOptions, onFilterChange }) => {
    const [isMenuVisible, setMenuVisible] = React.useState(false);
    const [isNewFilterVisible, setNewFilterVisible] = React.useState(false);

    const formRef = React.useRef<HTMLFormElement>(null);

    const matchTypeOptions = useMemo(() => {
        return Object.keys(MATCH_TYPES).map((key) => ({ 
            id: MATCH_TYPES[key as keyof typeof MATCH_TYPES], 
            name: MATCH_TYPES[key as keyof typeof MATCH_TYPES]
        }));
    }, []);

    const isFiltersValid = useMemo(() => {
        return filters.every((filter) => filter.filterValue.toString().length > 0);
    }, [filters]);

    const selectableFilters = useMemo(() => {
        return filterOptions.filter((option) => !filters.find((filter) => matchPhrases(option.id.toString(), filter.filterName) ))
    }, [filterOptions, filters]);

    const handleChangeFilterName = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const updatedFilters = filters.map((filter) => filter.filterName === e.target.name ? { ...filter, filterName: e.target.value } : filter)
        onFilterChange(updatedFilters)
    };

    const handleChangeFilterValue = (e: React.ChangeEvent<HTMLInputElement>) => {
        const updatedFilters = filters.map((filter) => filter.filterName === e.target.name ? { ...filter, filterValue: e.target.value } : filter)
        onFilterChange(updatedFilters)
    };

    const handleChangeFilterMatchType = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const updatedFilters = filters.map((filter) => filter.filterName === e.target.name ? { ...filter, filterMatchType: e.target.value } : filter)
        onFilterChange(updatedFilters)
    };

    const handleAddFilter = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const newFilterName = formRef.current?.newFilter.value;
        if (!newFilterName) return toastService.error('Select a filter!');

        onFilterChange([...filters, { filterName: newFilterName, filterValue: '', filterMatchType: MATCH_TYPES.CONTAINS }]);
        setNewFilterVisible(false);

        formRef.current.reset();
    };

    const handleDeleteFilter = (filterName: string) => {
        onFilterChange(filters.filter((filter) => filter.filterName !== filterName));
    };

    const handleClearFilters = () => {
        onFilterChange([]);
    };

    return ( 
        <div className={classNames.filter}>
            <Conditional isVisible={isMenuVisible}>
                <div className={classNames.filterOptions}>
                    <div className={classNames.filterOptionsHeader}>Filter</div>
                    <div className={classNames.filterOptionsBody}>
                        <Conditional isVisible={filters.length > 0}>
                            {filters.map((filter) => (
                                <div className={classNames.filterOptionsItem} key={filter.filterName}>
                                    <div className="container-fluid">
                                        <div className="row">
                                            <div className="col p-0">
                                                <Select
                                                    name={filter.filterName}
                                                    placeholder="Select Filter"
                                                    options={filterOptions}
                                                    value={filter.filterName}
                                                    onChange={handleChangeFilterName}
                                                />
                                            </div>

                                            <div className="col">
                                                <Select
                                                    name={filter.filterName}
                                                    placeholder="Select Condition"
                                                    options={matchTypeOptions}
                                                    value={filter.filterMatchType}
                                                    onChange={handleChangeFilterMatchType}
                                                />
                                            </div>

                                            <div className='col p-0'>
                                                <Input
                                                    type="text"
                                                    name={filter.filterName}
                                                    autoComplete="off"
                                                    value={filter.filterValue}
                                                    onChange={handleChangeFilterValue}
                                                    placeholder="Enter to search"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="ms-3">
                                        <div className={classNames.filterButtonDelete} onClick={() => handleDeleteFilter(filter.filterName)}>
                                            <i className="bi bi-trash"></i>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </Conditional>
                        <Conditional isVisible={filters.length === 0}>
                            <div className={classNames.filterOptionsBodyEmpty}>
                                No Filters Applied
                            </div>
                        </Conditional>
                    </div>
                    <div className={classNames.filterOptionsFooter}>
                        <div>
                            <Conditional isVisible={!isNewFilterVisible && isFiltersValid}>
                                <button className={classNames.filterButton} onClick={() => setNewFilterVisible(true)}>
                                    <i className="bi bi-plus"></i>
                                    <span>Add Filter</span>
                                </button>
                            </Conditional>
                        </div>

                        <Conditional isVisible={filters.length > 0}>
                            <div className="d-flex justify-content-center">
                                <Button 
                                    onClick={handleClearFilters} 
                                    disabled={!isFiltersValid} 
                                    label='Clear Filters' 
                                    icon='filter' 
                                    style={{ margin: 0}}
                                />
                            </div>
                        </Conditional>
                    </div>

                <Conditional isVisible={isNewFilterVisible}>
                        <form ref={formRef} onSubmit={handleAddFilter}>
                            <div className="my-4 divider"></div>

                            <div className="d-flex">
                                <div className="flex-fill me-3">
                                    <Select
                                        name="newFilter"
                                        placeholder="Select Filter"
                                        options={selectableFilters}
                                    />
                                </div>

                                <Button label='Add' icon='plus' type='submit' style={{ margin: 0}} />
                            </div>
                        </form>
                </Conditional>
                </div>
            </Conditional>

            <button className={classNames.filterButton} onClick={() => setMenuVisible((prev) => !prev)}>
                <Conditional isVisible={!isMenuVisible}>
                    <i className="bi bi-filter"></i>
                    <span>Show Filters</span>
                </Conditional>

                <Conditional isVisible={isMenuVisible}>
                    <span>Close Filter Menu</span>
                </Conditional>
            </button>
        </div>
    );
};




 
export default Filters;